import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import styles from './adminUserDetailModal.module.css';
import mainStyles from '../main.module.css';
import ScreenModal from '../../components/ScreenModal/screenModal';
import { AdminUser, AdminUserFull, AdminUserUpdateForm } from '../../services/http/managementUser.api';
import AlertModal, { alertModalInitialState, AlertModalProps } from '../../components/AlertModal';
import { ApiError } from '../../services/http';
import ButtonFileInput from '../../components/ButtonFileInput';
import Button from '../../components/Button/button';
import Input from '../../components/Input/input';
import { IconChangeStatus } from '../../services/http/user.api';
import { useGetDetailAdminUserApi, useUpdateAdminUserApi } from '../../hooks/api/managementUser.hook';
import { ManagementType, useAuthContext } from '../../store/auth.store';
import Formatter from '../../utils/formatters';
import { useCanvasImage } from '../../hooks/image.hook';
import { useMessageModal } from '../../hooks/modal.hook';
import Validator from '../../utils/validators';
import useEffectOnce from '../../hooks/useEffectOnce.hook';
import { useCreateLogApi } from '../../hooks/api/log.hook';
import { LogControlName, LogFormName } from '../../utils/log.utils';
import RegistrationUpdateInformation from '../../components/RegistrationUpdateInformation';
import Checkbox from '../../components/Checkbox';

/**
 * 管理者ユーザー更新のデフォルトフォーム
 */
export const DefaultAdminUserUpdateForm: AdminUserUpdateForm = {
  deleteFlg: false,
  email: '',
  icon: undefined,
  iconChangeStatus: IconChangeStatus.notChanged,
  id: '',
  userName: '',
};

/**
 * エラーラベルのインターフェース
 */
export interface ErrorLabelType {
  message: string,
  field?: string,
}

/**
 * 管理者ユーザー詳細モーダル コンポーネントの Props のインターフェース
 */
export interface Props {
  /** 管理者ユーザーの参照 */
  adminUser: AdminUser | null | undefined,
  /** キャンセルボタン押下時のハンドラ */
  handleCancel: () => void,
  /** 保存ボタン押下時のハンドラ */
  handleSuccess: () => void,
}

/**
 * 管理者ユーザー詳細モーダル コンポーネント
 */
export function Component({ adminUser: adminUserRef, handleCancel, handleSuccess }: Props) {
  const { user: currentUser } = useAuthContext();

  const [adminUser, setAdminUser] = useState<AdminUserFull | null>(null);
  const [form, setForm] = useState<AdminUserUpdateForm>(DefaultAdminUserUpdateForm);
  const [fileSelector, openFileSelector] = useState(false);
  const [userIcon, setUserIcon] = useState('');
  const [modal, setModal] = useState<AlertModalProps>(alertModalInitialState);
  const [error, setError] = useState<ErrorLabelType>({ message: '' });

  const adminUserGetDetailApi = useGetDetailAdminUserApi();
  const adminUserUpdateApi = useUpdateAdminUserApi();
  const { request: createLog } = useCreateLogApi();

  const userImage = useCanvasImage();
  const openMessageModal = useMessageModal();

  const isFormValid = useMemo(() => form.userName && form.email, [form]);

  const onSaveUserClick = useCallback(async () => {
    if (!adminUser) return;
    try {
      Validator.validUserName(form.userName);
    } catch (e) {
      setError({ field: 'ユーザー名', message: (e as Error).message });
      return;
    }
    try {
      Validator.validateEmail(form.email);
    } catch (e) {
      setError({ field: 'メールアドレス', message: (e as Error).message });
      return;
    }
    setModal({
      text: 'ユーザー情報を変更します。よろしいですか？',
      open: true,
      onCancel: () => setModal({ ...modal, open: false }),
      onConfirm: async () => {
        setModal({ ...modal, open: false });
        try {
          createLog(LogFormName.AdminUserDetail, LogControlName.Edit);

          await adminUserUpdateApi.request(form);
          handleSuccess();
          handleCancel();
        } catch (e) {
          setError({ message: (e as ApiError).message });
          openMessageModal((e as ApiError)?.message);
        }
      },
    });
  }, [form, modal]);

  const onFileSelected = useCallback(async (file: File | FileList | null) => {
    openFileSelector(false);
    if (file instanceof FileList) return;
    setForm({ ...form, icon: file || undefined, iconChangeStatus: file ? IconChangeStatus.changed : IconChangeStatus.deleted });
    if (file) {
      const fr = new FileReader();
      fr.onload = async () => {
        const finalUserImage = await userImage.getImage(fr.result as string);
        setUserIcon(finalUserImage as string);
        setForm({ ...form, icon: finalUserImage, iconChangeStatus: IconChangeStatus.changed });
      };
      fr.readAsDataURL(file);
    } else {
      setUserIcon('');
    }
  }, [userIcon, form, fileSelector]);

  const onDeleteImageClick = useCallback(async () => {
    setUserIcon('');
    setForm({ ...form, icon: undefined, iconChangeStatus: IconChangeStatus.deleted });
  }, [userIcon, form]);

  // ------------------EFFECTS-------------------
  useEffect(() => {
    setUserIcon('');
    setError({ message: '' });
    setAdminUser(null);
    setForm(DefaultAdminUserUpdateForm);
    if (!adminUserRef) return;
    try {
      adminUserGetDetailApi.request({ id: adminUserRef.id }).then((res) => {
        setAdminUser(res);
        setUserIcon(res.icon || '');
        setForm({
          deleteFlg: res.deleteFlg,
          email: res.email,
          // icon: res.icon,
          iconChangeStatus: IconChangeStatus.notChanged,
          id: res.id,
          userName: res.userName,
        });
      });
    } catch (e) {
      openMessageModal((e as ApiError)?.message);
      setError({ message: (e as ApiError).message });
    }
  }, [adminUserRef]);

  useEffectOnce(() => {
    createLog(LogFormName.AdminUserDetail, LogControlName.Show);
  });

  return (
    <ScreenModal
      customStyles={{
        minWidth: '800px',
        maxHeight: '85%',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
        border: 'var(--defaultBorder)',
        overflowY: 'auto',
      }}
    >
      <div className={styles['modal-header']}>
        <div className={styles.title}>
          管理者ユーザー詳細
        </div>
      </div>
      <div className={styles['modal-body']}>
        <div className={styles['col-1']}>
          <div className={styles.modalTabItem}>
            <div className={[styles.label, mainStyles['mb-10']].join(' ')}>
              ユーザー名
            </div>
            <Input className={styles['default-width']} value={form.userName} placeholder="20文字以内で入力してください" onChange={(value) => setForm({ ...form, userName: value })} />
          </div>
          <div className={styles.modalTabItem}>
            <div className={[styles.label, mainStyles['mb-10']].join(' ')}>
              メールアドレス
            </div>
            <div className={mainStyles['d-flex']}>
              <Input className={styles['default-width']} value={form.email} onChange={(value) => setForm({ ...form, email: value })} />
            </div>
          </div>
          <div className={styles.modalTabItem}>
            <div className={`${mainStyles['mt-4']} ${styles.text}`}>
              <Checkbox
                label="認証状態"
                value={!!adminUser?.emailConfirmed}
                onChange={() => {}}
              />
            </div>
          </div>
          <div className={styles.modalTabItem}>
            <div className={`${mainStyles['mt-4']} ${styles.text}`}>
              <Checkbox
                label="初期パスワード変更済み"
                value={!!adminUser?.passwordUpdate}
                onChange={() => {}}
              />
            </div>
          </div>
          <div className={styles.modalTabItem}>
            <div className={`${mainStyles['mt-4']} ${styles.text}`}>
              {currentUser?.userId !== adminUser?.id && (
                <Checkbox
                  label="無効にする"
                  value={form.deleteFlg}
                  onChange={() => setForm({ ...form, deleteFlg: !form.deleteFlg })}
                />
              )}
            </div>
          </div>
          <div className={[mainStyles['mt-4'], styles.label].join(' ')}>
            登録更新情報
          </div>
          <div className={[mainStyles['mt-3'], mainStyles['pl-3']].join(' ')}>
            <RegistrationUpdateInformation
              createdBy={adminUser?.registerUser ?? ''}
              createdAt={adminUser ? Formatter.toDisplayDate(adminUser.registerDate, Formatter.defaultDateTimeFormat) : ''}
              updatedBy={adminUser?.updateUser ?? ''}
              updatedAt={adminUser ? Formatter.toDisplayDate(adminUser.updateDate, Formatter.defaultDateTimeFormat) : ''}
            />
          </div>
        </div>
      </div>
      <div className={styles['modal-foooter']}>
        <div className={styles['mainframe-body-footer-buttons']}>
          <Button size="smaller" color="lighterGray" onClick={handleCancel}>キャンセル</Button>
          <div className="text-red">
            {error.field ? `${error.field}: ` : ''}
            {error.message}
          </div>
          <Button size="smaller" onClick={onSaveUserClick} loading={adminUserUpdateApi.loading} disabled={!isFormValid}>保存</Button>
        </div>
      </div>
      <AlertModal open={modal.open} text={modal.text} onCancel={modal.onCancel} onConfirm={modal.onConfirm} />
    </ScreenModal>
  );
}

export default Component;
